import React from 'react';

interface Props {
  /** Sample candidate row from /api/ai-search/sample-entity. */
  candidate: any | null;
  /** Columns currently included in the active filter group. */
  selectedColumns: Set<string>;
  /** Toggles ALL columns of a clicked cell in the active filter group. */
  onToggleColumns: (columns: string[]) => void;
}

const truncate = (s: string | null | undefined, n: number): string => {
  if (!s) return '';
  return s.length > n ? s.slice(0, n).trimEnd() + '…' : s;
};

const formatColumnLabel = (col: string): string =>
  col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/**
 * A region of the visual profile that maps to one or more searchable columns.
 * On hover (or when selected), shows a tiny badge with the column name(s);
 * click toggles inclusion of those columns in the active filter group.
 *
 * The cell visually inherits whatever its children look like — the wrapper
 * only adds a soft border + hover/selected state, so the schematic still
 * reads like the entity side panel rather than a custom form.
 */
const PickableCell: React.FC<{
  columns: string[];
  selectedColumns: Set<string>;
  onToggleColumns: (cols: string[]) => void;
  children: React.ReactNode;
  className?: string;
}> = ({ columns, selectedColumns, onToggleColumns, children, className }) => {
  const allSelected = columns.length > 0 && columns.every(c => selectedColumns.has(c));
  const someSelected = !allSelected && columns.some(c => selectedColumns.has(c));

  const stateClass = allSelected
    ? 'border-amber-400 bg-amber-50/70 ring-1 ring-amber-200'
    : someSelected
      ? 'border-amber-300 bg-amber-50/40'
      : 'border-transparent hover:border-blue-300 hover:bg-blue-50/40';

  const label = columns.map(formatColumnLabel).join(' · ');

  return (
    <button
      type="button"
      onClick={() => onToggleColumns(columns)}
      className={`group relative w-full text-left rounded-lg border-2 transition-colors p-2 ${stateClass} ${className || ''}`}
      title={`${allSelected ? 'Click to remove' : 'Click to add'}: ${columns.join(', ')}`}
      aria-pressed={allSelected}
    >
      <span
        className={`absolute top-1 right-1 px-1.5 py-0.5 text-[10px] font-medium rounded transition-opacity pointer-events-none ${
          allSelected
            ? 'bg-amber-200 text-amber-900 opacity-100'
            : someSelected
              ? 'bg-amber-100 text-amber-800 opacity-100'
              : 'bg-gray-700 text-white opacity-0 group-hover:opacity-90'
        }`}
      >
        {label}
      </span>
      {children}
    </button>
  );
};

const Empty: React.FC = () => <span className="text-gray-400 italic">—</span>;

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5 px-2">{children}</h4>
);

const CandidatePickerSchematic: React.FC<Props> = ({ candidate, selectedColumns, onToggleColumns }) => {
  if (!candidate) {
    return (
      <p className="text-xs text-gray-500 italic">
        No sample candidate available — add candidates to the project to see the visual picker.
      </p>
    );
  }

  const c = candidate;
  const sectionProps = { selectedColumns, onToggleColumns };

  return (
    <div className="space-y-3">
      {/* Profile header — name, headline, then current role and company as
          separate cells so users can search the role title without also
          searching every company in the database. */}
      <div>
        <PickableCell columns={['name']} {...sectionProps}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
              {(c.first_name?.[0] || c.name?.[0] || '?').toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 truncate">{c.name || <Empty />}</p>
            </div>
          </div>
        </PickableCell>

        <PickableCell columns={['headline']} {...sectionProps} className="mt-1">
          <p className="text-sm text-gray-600 italic">{c.headline || <Empty />}</p>
        </PickableCell>

        <div className="grid grid-cols-2 gap-1 mt-1">
          <PickableCell columns={['current_position']} {...sectionProps}>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide">Current role</p>
            <p className="text-sm text-gray-700 truncate">{c.current_position || <Empty />}</p>
          </PickableCell>
          <PickableCell columns={['current_company']} {...sectionProps}>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide">Current company</p>
            <p className="text-sm text-gray-700 truncate">{c.current_company || <Empty />}</p>
          </PickableCell>
        </div>
      </div>

      {/* Contact information */}
      <div>
        <SectionHeader>Contact information</SectionHeader>
        <div className="grid grid-cols-2 gap-1">
          <PickableCell columns={['email']} {...sectionProps}>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide">Work email</p>
            <p className="text-xs text-gray-700 truncate">{c.email || <Empty />}</p>
          </PickableCell>
          <PickableCell columns={['personal_email']} {...sectionProps}>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide">Personal email</p>
            <p className="text-xs text-gray-700 truncate">{c.personal_email || <Empty />}</p>
          </PickableCell>
          <PickableCell columns={['phone']} {...sectionProps}>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide">Phone</p>
            <p className="text-xs text-gray-700 truncate">{c.phone || <Empty />}</p>
          </PickableCell>
          <PickableCell columns={['li_profile_url']} {...sectionProps}>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide">LinkedIn</p>
            <p className="text-xs text-blue-600 truncate">{c.li_profile_url ? 'LinkedIn Profile' : <Empty />}</p>
          </PickableCell>
        </div>
      </div>

      {/* Summary */}
      <div>
        <SectionHeader>Summary</SectionHeader>
        <PickableCell columns={['summary']} {...sectionProps}>
          <p className="text-xs text-gray-700 whitespace-pre-line">
            {c.summary ? truncate(c.summary, 260) : <Empty />}
          </p>
        </PickableCell>
      </div>

      {/* Experience — each role is visually one card but split into three
          pickable strips (title, company, description) so users can target
          each independently. The AI itself often picks only a subset (e.g.
          description but not title), and the chip list reflects that. */}
      <div>
        <SectionHeader>Experience</SectionHeader>
        <div className="space-y-3">
          {/* Current role */}
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500 mt-3 flex-shrink-0" />
            <div className="min-w-0 flex-1 space-y-0.5">
              <PickableCell columns={['current_position']} {...sectionProps}>
                <p className="text-xs font-semibold text-gray-900">{c.current_position || <Empty />}</p>
              </PickableCell>
              <PickableCell columns={['current_company']} {...sectionProps}>
                <p className="text-xs text-gray-600">{c.current_company || <Empty />}</p>
              </PickableCell>
              <PickableCell columns={['current_position_description']} {...sectionProps}>
                <p className="text-[11px] text-gray-500 line-clamp-3">
                  {c.current_position_description ? truncate(c.current_position_description, 200) : <Empty />}
                </p>
              </PickableCell>
            </div>
          </div>

          {/* Previous roles 1..3 */}
          {[1, 2, 3].map(n => {
            const titleCol = `previous_position_${n}`;
            const companyCol = `previous_company_${n}`;
            const descCol = `previous_position_${n}_description`;
            return (
              <div key={n} className="flex items-start gap-2">
                <div className="w-2 h-2 rounded-full bg-gray-300 mt-3 flex-shrink-0" />
                <div className="min-w-0 flex-1 space-y-0.5">
                  <PickableCell columns={[titleCol]} {...sectionProps}>
                    <p className="text-xs font-semibold text-gray-900">{c[titleCol] || <Empty />}</p>
                  </PickableCell>
                  <PickableCell columns={[companyCol]} {...sectionProps}>
                    <p className="text-xs text-gray-600">{c[companyCol] || <Empty />}</p>
                  </PickableCell>
                  <PickableCell columns={[descCol]} {...sectionProps}>
                    <p className="text-[11px] text-gray-500 line-clamp-2">
                      {c[descCol] ? truncate(c[descCol], 160) : <Empty />}
                    </p>
                  </PickableCell>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Education — institution / degree / field as three independent cells */}
      <div>
        <SectionHeader>Education</SectionHeader>
        <div className="space-y-1">
          <PickableCell columns={['education_1']} {...sectionProps}>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide">Institution</p>
            <p className="text-xs font-semibold text-gray-900">{c.education_1 || <Empty />}</p>
          </PickableCell>
          <div className="grid grid-cols-2 gap-1">
            <PickableCell columns={['degree_1']} {...sectionProps}>
              <p className="text-[11px] text-gray-400 uppercase tracking-wide">Degree</p>
              <p className="text-xs text-gray-700">{c.degree_1 || <Empty />}</p>
            </PickableCell>
            <PickableCell columns={['field_of_study_1']} {...sectionProps}>
              <p className="text-[11px] text-gray-400 uppercase tracking-wide">Field</p>
              <p className="text-xs text-gray-700">{c.field_of_study_1 || <Empty />}</p>
            </PickableCell>
          </div>
        </div>
      </div>

      {/* Skills */}
      <div>
        <SectionHeader>Skills</SectionHeader>
        <PickableCell columns={['all_skills_text', 'top_skills_10']} {...sectionProps}>
          {c.all_skills_text ? (
            <div className="flex flex-wrap gap-1">
              {c.all_skills_text.split(',').slice(0, 12).map((s: string, i: number) => (
                <span key={i} className="px-1.5 py-0.5 text-[10px] rounded-full bg-blue-50 text-blue-700">
                  {s.trim()}
                </span>
              ))}
            </div>
          ) : (
            <Empty />
          )}
        </PickableCell>
      </div>

      {/* Certifications */}
      <div>
        <SectionHeader>Certifications</SectionHeader>
        <PickableCell columns={['certification_1']} {...sectionProps}>
          <p className="text-xs text-gray-700">{c.certification_1 || <Empty />}</p>
        </PickableCell>
      </div>
    </div>
  );
};

// Columns the candidate picker exposes as clickable cells. Used by the
// modal's "+ more fields" expander to derive the long-tail list.
export const CANDIDATE_PICKER_COLUMNS: string[] = [
  'name', 'headline', 'current_position', 'current_company',
  'email', 'personal_email', 'phone', 'li_profile_url',
  'summary',
  'current_position_description',
  'previous_position_1', 'previous_company_1', 'previous_position_1_description',
  'previous_position_2', 'previous_company_2', 'previous_position_2_description',
  'previous_position_3', 'previous_company_3', 'previous_position_3_description',
  'education_1', 'degree_1', 'field_of_study_1',
  'all_skills_text', 'top_skills_10',
  'certification_1'
];

export default CandidatePickerSchematic;
